import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import Swal from 'sweetalert2';
// Importar pestañas existentes de NuevoCliente
import ContactoTab from '@/pages/clientes/NuevoCliente/ContactoTab.jsx';
import NotificacionesTab from '@/pages/clientes/NuevoCliente/NotificacionesTab.jsx';
import TarifasTab from '@/pages/clientes/NuevoCliente/TarifasTab.jsx';
import DomiciliosTab from '@/pages/clientes/NuevoCliente/DomiciliosTab.jsx';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Button } from '@/components/ui/button.jsx';
// Usar el Select de FormComponents (select nativo compatible con react-hook-form)
import { Input, Select } from '@/components/ui/FormComponents.jsx';
import Combobox from '@/components/ui/Combobox.jsx';
import Label from '@/components/ui/label.jsx';
import AdminPageHeader from '../ui/AdminPageHeader.jsx';
import { M_CREATE_CLIENTE_COMPLETO, M_UPDATE_CLIENTE_COMPLETO, M_CREATE_CLIENTE_SUBCUENTA, M_UPDATE_CLIENTE_SUBCUENTA, Q_CLIENTE } from './clientes.gql.js';
import { getProvinces, getLocalities } from '@/services/clients.js';
import { listSucursalesOperativas } from '@/services/sucursalesOperativas.js';
import { useUI } from '@/context/UIContext.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { isTenantFeatureEnabled } from '@/config/tenantFeatures.js';
import { RETORNABLES_CUENTA_QUERY, RETORNABLES_TIPOS_QUERY } from '@/graphql/retornables.gql.js';
import RetornablesMonitor from '@/pages/Retornables/RetornablesMonitor.jsx';
import AccountStatement from '@/components/CuentaCorriente/AccountStatement.jsx';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const IVA_CONDITIONS = [
  { value: 'RI', label: 'Responsable Inscripto' },
  { value: 'MT', label: 'Monotributista' },
  { value: 'EX', label: 'Exento' },
  { value: 'CF', label: 'Consumidor Final' },
  { value: 'RNI', label: 'Responsable No Inscripto' },
];

function sanitizeOptional(value) {
  if (typeof value !== 'string') {
    return value ?? null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseOptionalNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function parseOptionalInt(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function safeParseJson(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function buildNotificacionesConfig(data) {
  if (!data) return null;
  return {
    destinosGenerales: JSON.stringify(data.destinosGenerales || {}),
    eventosConfig: JSON.stringify(data.avisosPorEstado || {}),
    noEnviarFueraHorario: data.noEnviarFueraHorario || false,
    horarioDesde: data.horarioDesde || null,
    horarioHasta: data.horarioHasta || null,
    activo: true,
  };
}

function buildTarifasConfig(data) {
  if (!data) return null;
  return {
    tarifaBaseId: data.tarifaBaseId || null,
    descuentoPct: parseOptionalNumber(data.descuentoPct),
    recargoPct: parseOptionalNumber(data.recargoPct),
    requiereConfirmacion: data.requiereConfirmacion || false,
    prioridadDespacho: data.prioridadDespacho || false,
    prioridadEntrega: data.prioridadEntrega || false,
    observaciones: data.observaciones || null,
    activo: true,
  };
}

const formatCUIT = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length !== 11) return raw;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

export default function ClienteForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { setActiveModule } = useUI();
  const { tenant, can } = useAuth();

  const [{ data, fetching, error }] = useQuery({
    query: Q_CLIENTE,
    variables: { id: id ?? '' },
    pause: !isEditing,
  });

  const [, createCliente] = useMutation(M_CREATE_CLIENTE_COMPLETO);
  const [, updateCliente] = useMutation(M_UPDATE_CLIENTE_COMPLETO);
  const [, createSubcuenta] = useMutation(M_CREATE_CLIENTE_SUBCUENTA);
  const [, updateSubcuenta] = useMutation(M_UPDATE_CLIENTE_SUBCUENTA);
  const [subcuentasData, setSubcuentasData] = useState([]);
  const [editingSubcuenta, setEditingSubcuenta] = useState(null);

  const returnablesFeatureEnabled = isTenantFeatureEnabled(tenant, { featureCode: 'STOCK.RETORNABLES' });
  const canViewReturnables = can('RETORNABLES_VER');
  const shouldCheckReturnables = isEditing && canViewReturnables;
  const [returnablesTypesResult] = useQuery({
    query: RETORNABLES_TIPOS_QUERY,
    variables: {},
    pause: !shouldCheckReturnables,
  });
  const [returnablesAccountResult] = useQuery({
    query: RETORNABLES_CUENTA_QUERY,
    variables: { filtro: { clienteId: id } },
    pause: !shouldCheckReturnables,
    requestPolicy: 'network-only',
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      razonSocial: '',
      nombreFantasia: '',
      tipoDocumento: '',
      cuit: '',
      condicionIVA: '',
      categoriaCliente: 'Minorista',
      codigo: '',
      tipoCliente: '',
      estado: 'ACTIVO',
      motivoEstado: '',
      cp: '',
      lat: '',
      lng: '',
      provinciaId: '',
      localidadId: '',
      codigoPostal: '',
      email: '',
      telefono: '',
      celular: '',
      direccion: '',
      sucursal: '',
      plazoPago: '',
      condicionPago: 'CONTADO',
      limiteCredito: '',
      diasVencimiento: '',
      toleranciaVencidos: '',
      prioridadOperativa: 'NORMAL',
      bloqueoPorDeuda: 'false',
      umbralDeuda: '',
      slaDespachoHs: '',
      slaEntregaHs: '',
      activo: 'true',
    },
  });

  useEffect(() => {
    if (data?.cliente) {
      const cliente = data.cliente;
      
      const formValues = {
        razonSocial: cliente.razonSocial ?? '',
        nombreFantasia: cliente.nombreFantasia ?? '',
        tipoDocumento: cliente.tipoDocumento ?? '',
        cuit: cliente.cuit ?? '',
        condicionIVA: cliente.condicionIVA ?? '',
        categoriaCliente: cliente.categoriaCliente ?? 'Minorista',
        codigo: cliente.codigo ?? '',
        tipoCliente: cliente.tipoCliente ?? '',
        estado: cliente.estado ?? 'ACTIVO',
        motivoEstado: cliente.motivoEstado ?? '',
        cp: cliente.cp ?? '',
        lat: cliente.lat ?? '',
        lng: cliente.lng ?? '',
        provinciaId: cliente.provinciaId ?? '',
        localidadId: cliente.localidadId ?? '',
        codigoPostal: cliente.codigoPostal ?? '',
        email: cliente.emails?.[0] ?? '',
        telefono: cliente.telefono ?? '',
        celular: cliente.celular ?? '',
        direccion: cliente.direccion ?? '',
        sucursal: cliente.sucursal ?? '',
        plazoPago: cliente.plazoPago ?? '',
        condicionPago: cliente.condicionPago ?? 'CONTADO',
        limiteCredito: cliente.limiteCredito ?? '',
        diasVencimiento: cliente.diasVencimiento ?? '',
        toleranciaVencidos: cliente.toleranciaVencidos ?? '',
        prioridadOperativa: cliente.prioridadOperativa ?? 'NORMAL',
        bloqueoPorDeuda: cliente.bloqueoPorDeuda ? 'true' : 'false',
        umbralDeuda: cliente.umbralDeuda ?? '',
        slaDespachoHs: cliente.slaDespachoHs ?? '',
        slaEntregaHs: cliente.slaEntregaHs ?? '',
        activo: cliente.activo ? 'true' : 'false',
      };
      
      reset(formValues);

      setContactosData(cliente.contactos || []);
      setDomiciliosData(cliente.domicilios || []);
      setNotificacionesData({
        destinosGenerales: safeParseJson(cliente.notificacionesConfig?.destinosGenerales, {}),
        avisosPorEstado: safeParseJson(cliente.notificacionesConfig?.eventosConfig, {}),
        aplicarATodos: false,
        noEnviarFueraHorario: cliente.notificacionesConfig?.noEnviarFueraHorario || false,
        horarioDesde: cliente.notificacionesConfig?.horarioDesde || '',
        horarioHasta: cliente.notificacionesConfig?.horarioHasta || '',
      });
      setTarifasData({
        tarifaBaseId: cliente.tarifasConfig?.tarifaBaseId || '',
        descuentoPct: cliente.tarifasConfig?.descuentoPct || '',
        recargoPct: cliente.tarifasConfig?.recargoPct || '',
        requiereConfirmacion: cliente.tarifasConfig?.requiereConfirmacion || false,
        prioridadDespacho: cliente.tarifasConfig?.prioridadDespacho || false,
        prioridadEntrega: cliente.tarifasConfig?.prioridadEntrega || false,
        observaciones: cliente.tarifasConfig?.observaciones || '',
      });
      setSubcuentasData(cliente.subcuentas || []);
      
      // Formatear CUIT si es necesario
      if (cliente.tipoDocumento === 'CUIT' && cliente.cuit && !cliente.cuit.includes('-')) {
        const formatted = formatCUIT(cliente.cuit);
        setValue('cuit', formatted, { shouldDirty: false, shouldValidate: false });
      }
    }
  }, [data, reset, setValue]);

  // Estados para pestañas adicionales (Notificaciones, Tarifas)
  const [activeTab, setActiveTab] = useState('general');
  const [notificacionesData, setNotificacionesData] = useState({});
  const [tarifasData, setTarifasData] = useState({});
  const [contactosData, setContactosData] = useState([]);
  const [domiciliosData, setDomiciliosData] = useState([]);
  const [showGeo, setShowGeo] = useState(false);

  // Provincias / Localidades
  const [provinces, setProvinces] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);

  const [sucursales, setSucursales] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [sucursalesError, setSucursalesError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSucursales([]);
    setLoadingSucursales(true);
    setSucursalesError('');
    listSucursalesOperativas()
      .then(rows => { if (!cancelled) setSucursales(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setSucursalesError('No se pudieron cargar las sucursales.'); })
      .finally(() => { if (!cancelled) setLoadingSucursales(false); });
    return () => { cancelled = true; };
  }, [tenant?.id, tenant?.code, tenant?.codigo]);

  const sucursalOptions = sucursales.map(sucursal => ({
    value: String(sucursal.id),
    label: [sucursal.codigo || sucursal.numero_sucursal, sucursal.nombre, sucursal.localidad_nombre].filter(Boolean).join(' — '),
  }));

  const watchTipoDocumento = watch('tipoDocumento');
  const watchProvinciaId = watch('provinciaId');

  useEffect(() => {
    setActiveModule('Administración');
  }, [setActiveModule]);

  // Cargar provincias al montar
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const prov = await getProvinces();
        if (mounted) setProvinces(prov || []);
      } catch (err) {
        console.error('Error cargando provincias:', err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Cargar localidades cuando cambie provinciaId
  useEffect(() => {
    if (!watchProvinciaId) {
      setLocalities([]);
      return;
    }
    let mounted = true;
    (async () => {
      setIsLoadingLocalities(true);
      try {
        const locs = await getLocalities(watchProvinciaId);
        if (mounted) setLocalities(locs || []);
      } catch (err) {
        console.error('Error cargando localidades:', err);
      } finally {
        if (mounted) setIsLoadingLocalities(false);
      }
    })();
    return () => { mounted = false; };
  }, [watchProvinciaId]);

  // Cuando cambia el tipo de documento, si es CUIT formatear el campo cuit
  useEffect(() => {
    if (watchTipoDocumento === 'CUIT') {
      const currentCuit = watch('cuit');
      if (currentCuit && !currentCuit.includes('-')) {
        const formatted = formatCUIT(currentCuit);
        setValue('cuit', formatted, { shouldDirty: false, shouldValidate: false });
      }
    }
  }, [watchTipoDocumento, watch, setValue]);

  const saveSubcuenta = async subcuenta => {
    const input = {
      codigo: subcuenta.codigo?.trim(),
      nombre: subcuenta.nombre?.trim(),
      descripcion: subcuenta.descripcion?.trim() || null,
      activo: subcuenta.activo !== false,
      sucursalResponsableId: subcuenta.sucursalResponsableId || null,
      condicionPago: subcuenta.condicionPago?.trim() || null,
      observaciones: subcuenta.observaciones?.trim() || null,
    };
    if (!input.codigo || !input.nombre) { await Swal.fire('Error', 'Indicá código y nombre para la subcuenta.', 'error'); return; }
    const result = subcuenta.id ? await updateSubcuenta({ clienteId: id, id: subcuenta.id, input }) : await createSubcuenta({ clienteId: id, input });
    if (result.error) { await Swal.fire('Error', result.error.message, 'error'); return; }
    const saved = result.data?.updateClienteSubcuenta || result.data?.createClienteSubcuenta;
    setSubcuentasData(current => subcuenta.id ? current.map(item => item.id === saved.id ? saved : item) : [...current, saved]);
    setEditingSubcuenta(null);
  };

  const toggleSubcuenta = async subcuenta => {
    const input = {
      codigo: subcuenta.codigo,
      nombre: subcuenta.nombre,
      descripcion: subcuenta.descripcion || null,
      activo: !subcuenta.activo,
      sucursalResponsableId: subcuenta.sucursalResponsableId || null,
      condicionPago: subcuenta.condicionPago || null,
      observaciones: subcuenta.observaciones || null,
    };
    const result = await updateSubcuenta({ clienteId: id, id: subcuenta.id, input });
    if (result.error) { await Swal.fire('Error', result.error.message, 'error'); return; }
    const saved = result.data?.updateClienteSubcuenta;
    setSubcuentasData(current => current.map(item => item.id === saved.id ? saved : item));
  };

  const onSubmit = async formData => {
    const invalidContacto = contactosData.find(
      contacto => contacto.email && !emailPattern.test(contacto.email)
    );
    if (invalidContacto) {
      await Swal.fire('Error', 'Hay un email inválido en los contactos.', 'error');
      return;
    }

    const razonSocial = formData.razonSocial.trim();
    if (!razonSocial) {
      setError('razonSocial', { type: 'manual', message: 'Ingresá la razón social del cliente.' });
      return;
    }

    if (formData.estado && formData.estado !== 'ACTIVO' && !formData.motivoEstado?.trim()) {
      setError('motivoEstado', { type: 'manual', message: 'Indicá el motivo del estado.' });
      return;
    }

    const contactoPrincipal =
      contactosData.find(contacto => contacto.principal) ||
      contactosData.find(contacto => contacto.email || contacto.telefono || contacto.celular);
    const clienteInput = {
      razonSocial,
      nombreFantasia: sanitizeOptional(formData.nombreFantasia),
      tipoDocumento: formData.tipoDocumento,
      cuit: sanitizeOptional(formData.cuit),
      condicionIVA: sanitizeOptional(formData.condicionIVA),
      codigo: sanitizeOptional(formData.codigo),
      estado: sanitizeOptional(formData.estado),
      motivoEstado: formData.estado !== 'ACTIVO' ? sanitizeOptional(formData.motivoEstado) : null,
      cp: sanitizeOptional(formData.cp),
      lat: parseOptionalNumber(formData.lat),
      lng: parseOptionalNumber(formData.lng),
      // categoriaCliente: no lo enviamos explícitamente desde el cliente de edición
      emails: contactoPrincipal?.email ? [contactoPrincipal.email.trim()] : [],
      telefono: sanitizeOptional(contactoPrincipal?.telefono),
      celular: sanitizeOptional(contactoPrincipal?.celular),
      direccion: sanitizeOptional(formData.direccion),
      provinciaId: sanitizeOptional(formData.provinciaId),
      localidadId: sanitizeOptional(formData.localidadId),
      codigoPostal: sanitizeOptional(formData.codigoPostal),
  // categoriaCliente: obligatorio en ClienteInput
      categoriaCliente: sanitizeOptional(formData.categoriaCliente) || 'Minorista',
      activo: formData.activo === 'true',
      sucursal: sanitizeOptional(formData.sucursal),
      plazoPago: parseOptionalInt(formData.plazoPago),
      limiteCredito: parseOptionalNumber(formData.limiteCredito),
      condicionPago: sanitizeOptional(formData.condicionPago),
      diasVencimiento: parseOptionalInt(formData.diasVencimiento),
      toleranciaVencidos: parseOptionalInt(formData.toleranciaVencidos),
      prioridadOperativa: sanitizeOptional(formData.prioridadOperativa),
      bloqueoPorDeuda: formData.bloqueoPorDeuda === 'true',
      umbralDeuda: parseOptionalNumber(formData.umbralDeuda),
      slaDespachoHs: parseOptionalInt(formData.slaDespachoHs),
      slaEntregaHs: parseOptionalInt(formData.slaEntregaHs),
    };

    const input = {
      cliente: clienteInput,
      contactos: contactosData,
      domicilios: domiciliosData,
      notificacionesConfig: buildNotificacionesConfig(notificacionesData),
      tarifasConfig: buildTarifasConfig(tarifasData),
    };

    try {
      let result;
      if (isEditing) {
        result = await updateCliente({ id, input });
      } else {
        result = await createCliente({ input });
      }

      if (result.error) {
        await Swal.fire('Error', result.error.message, 'error');
        return;
      }

      const success = isEditing ? 'Cliente actualizado correctamente.' : 'Cliente creado correctamente.';
      await Swal.fire('Éxito', success, 'success');
      if (location.state?.returnTo) {
        navigate(location.state.returnTo, {
          state: {
            refreshClientId: result.data?.updateClienteCompleto?.id || result.data?.createClienteCompleto?.id || id,
          },
        });
      } else {
        navigate('/admin/clientes');
      }
    } catch (mutationError) {
      console.error(mutationError);
      await Swal.fire('Error', 'Ocurrió un error al guardar el cliente.', 'error');
    }
  };

  const returnablesAccount = returnablesAccountResult.data?.retornablesCuentaCorriente;
  const hasReturnablesEvidence = (returnablesTypesResult.data?.retornablesTipos || []).length > 0
    || (returnablesAccount?.saldos || []).length > 0
    || (returnablesAccount?.items || []).length > 0
    || (returnablesAccount?.aging || []).length > 0;
  const showReturnablesTab = shouldCheckReturnables && (returnablesFeatureEnabled || hasReturnablesEvidence);
  const pageTitle = isEditing ? 'Editar cliente' : 'Nuevo cliente';

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4">
      <AdminPageHeader
        title={pageTitle}
        breadcrumbs={[
          { label: 'Administración', href: '/admin' },
          { label: 'Clientes', href: '/admin/clientes' },
          { label: isEditing ? 'Editar' : 'Nuevo' },
        ]}
        action={
          <Button
            variant="ghost"
            asChild
            disabled={isSubmitting}
            className="border border-[rgba(var(--border),0.7)] bg-transparent text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.7)]"
          >
            <Link to="/admin/clientes">Cancelar</Link>
          </Button>
        }
      />

      {isEditing && fetching ? (
        <Card className="rounded-2xl">
          <CardContent className="py-12 text-center text-[rgba(var(--muted),0.9)]">
            Cargando datos del cliente...
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="rounded-2xl">
          <CardContent className="space-y-3 py-10 text-center">
            <p className="text-lg font-semibold text-[rgb(var(--brand))]">No se pudo cargar el cliente.</p>
            <p className="text-sm text-[rgba(var(--muted),0.9)]">{error.message}</p>
            <Button
              variant="ghost"
              className="border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.85)]"
              onClick={() => navigate('/admin/clientes')}
            >
              Volver al listado
            </Button>
          </CardContent>
        </Card>
      ) : isEditing && !data?.cliente ? (
        <Card className="rounded-2xl">
          <CardContent className="space-y-3 py-10 text-center">
            <p className="text-lg font-semibold text-[rgb(var(--fg))]">Cliente no encontrado.</p>
            <Button
              variant="ghost"
              className="border border-[rgba(var(--border),0.7)] bg-[rgba(var(--panel),0.9)] text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.85)]"
              onClick={() => navigate('/admin/clientes')}
            >
              Volver al listado
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-2xl">
          <CardContent>
            {/* Pestañas similar a NuevoCliente */}
            <div className="space-y-6">
              <div className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.85)] shadow-[0_16px_36px_rgba(var(--shadow),0.08)]">
                <div className="flex flex-wrap border-b border-[rgba(var(--border),0.6)]">
                  <button
                    onClick={() => setActiveTab('general')}
                    aria-current={activeTab === 'general' ? 'page' : undefined}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                      activeTab === 'general'
                        ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                        : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                    }`}
                  >
                    General
                  </button>
                  <button
                    onClick={() => setActiveTab('contacto')}
                    aria-current={activeTab === 'contacto' ? 'page' : undefined}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                      activeTab === 'contacto'
                        ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                        : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                    }`}
                  >
                    Contacto
                  </button>
                  <button
                    onClick={() => setActiveTab('domicilios')}
                    aria-current={activeTab === 'domicilios' ? 'page' : undefined}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                      activeTab === 'domicilios'
                        ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                        : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                    }`}
                  >
                    Domicilios
                  </button>
                  <button
                    onClick={() => setActiveTab('comercial')}
                    aria-current={activeTab === 'comercial' ? 'page' : undefined}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                      activeTab === 'comercial'
                        ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                        : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                    }`}
                  >
                    Comercial
                  </button>
                                    {isEditing ? (
                    <button type="button" onClick={() => setActiveTab('subcuentas')} aria-current={activeTab === 'subcuentas' ? 'page' : undefined}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${activeTab === 'subcuentas' ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]' : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'}`}>
                      Subcuentas
                    </button>
                  ) : null}
                  <button
                    onClick={() => setActiveTab('notificaciones')}
                    aria-current={activeTab === 'notificaciones' ? 'page' : undefined}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                      activeTab === 'notificaciones'
                        ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                        : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                    }`}
                  >
                    Notificaciones
                  </button>
                  <button
                    onClick={() => setActiveTab('tarifas')}
                    aria-current={activeTab === 'tarifas' ? 'page' : undefined}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                      activeTab === 'tarifas'
                        ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                        : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                    }`}
                  >
                    Tarifas
                  </button>
                  {showReturnablesTab ? (
                    <button
                      type="button"
                      onClick={() => setActiveTab('retornables')}
                      aria-current={activeTab === 'retornables' ? 'page' : undefined}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                        activeTab === 'retornables'
                          ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                          : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                      }`}
                    >
                      Retornables
                    </button>
                  ) : null}
                  {isEditing ? (
                    <button
                      type="button"
                      onClick={() => setActiveTab('cuenta-corriente')}
                      aria-current={activeTab === 'cuenta-corriente' ? 'page' : undefined}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${
                        activeTab === 'cuenta-corriente'
                          ? 'border-b-2 border-[rgb(var(--brand))] text-[rgb(var(--brand))]'
                          : 'text-[rgba(var(--muted),0.9)] hover:bg-[rgba(var(--subpanel),0.7)] hover:text-[rgb(var(--fg))]'
                      }`}
                    >
                      Cuenta corriente
                    </button>
                  ) : null}
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Contenido de cada pestaña: reutilizamos los campos registrados con react-hook-form */}
                {activeTab === 'general' && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="razonSocial">Razón social *</Label>
                      <Input
                        id="razonSocial"
                        placeholder="Razón social"
                        {...register('razonSocial', { required: 'Ingresá la razón social del cliente.' })}
                      />
                      {errors.razonSocial ? (
                        <p className="text-sm text-[rgb(var(--brand))]">{errors.razonSocial.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nombreFantasia">Nombre de fantasía</Label>
                      <Input id="nombreFantasia" placeholder="Opcional" {...register('nombreFantasia')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigo">Código de cliente</Label>
                      <Input id="codigo" placeholder="Ej. CLI-10058 (vacío: se asigna el ID)" {...register('codigo')} />
                      <p className="text-xs text-[rgba(var(--muted),0.9)]">Opcional al crear; si queda vacío se asigna automáticamente el ID interno.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estado">Estado</Label>
                      <Select id="estado" {...register('estado')} value={watch('estado') || 'ACTIVO'}>
                        <option value="ACTIVO">Activo</option>
                        <option value="SUSPENDIDO">Suspendido</option>
                        <option value="BLOQUEADO">Bloqueado</option>
                      </Select>
                    </div>
                    {watch('estado') && watch('estado') !== 'ACTIVO' ? (
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="motivoEstado">Motivo del estado</Label>
                        <Input id="motivoEstado" placeholder="Detalle del bloqueo" {...register('motivoEstado')} />
                        {errors.motivoEstado ? (
                          <p className="text-sm text-[rgb(var(--brand))]">{errors.motivoEstado.message}</p>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="space-y-2">
                      <Label htmlFor="tipoDocumento">Tipo de documento *</Label>
                      <Select 
                        id="tipoDocumento" 
                        {...register('tipoDocumento', { required: 'Seleccioná un tipo.' })}
                        value={watch('tipoDocumento') || ''}
                      >
                        <option value="">Seleccionar</option>
                        <option value="CUIT">CUIT</option>
                        <option value="DNI">DNI</option>
                        <option value="Pasaporte">Pasaporte</option>
                      </Select>
                      {errors.tipoDocumento ? (
                        <p className="text-sm text-[rgb(var(--brand))]">{errors.tipoDocumento.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cuit">Número *</Label>
                      <Input
                        id="cuit"
                        placeholder="CUIT o DNI"
                        {...register('cuit', { required: 'Ingresá el número de documento.' })}
                        onBlur={(e) => {
                          if (watchTipoDocumento === 'CUIT') {
                            const formatted = formatCUIT(e.target.value);
                            setValue('cuit', formatted, { shouldDirty: true });
                          }
                        }}
                      />
                      {errors.cuit ? <p className="text-sm text-[rgb(var(--brand))]">{errors.cuit.message}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="condicionIVA">Condición IVA *</Label>
                      <Select 
                        id="condicionIVA" 
                        {...register('condicionIVA', { required: 'Ingresá la condición IVA.' })}
                        value={watch('condicionIVA') || ''}
                      >
                        <option value="">Seleccionar...</option>
                        {IVA_CONDITIONS.map(condition => (
                          <option key={condition.value} value={condition.value}>{condition.label}</option>
                        ))}
                      </Select>
                      {errors.condicionIVA ? (
                        <p className="text-sm text-[rgb(var(--brand))]">{errors.condicionIVA.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="direccion">Dirección</Label>
                      <Input id="direccion" placeholder="Calle 123" {...register('direccion')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="codigoPostal">Código Postal</Label>
                      <Input id="codigoPostal" placeholder="1043" {...register('codigoPostal')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="provinciaId">Provincia</Label>
                      <Select id="provinciaId" {...register('provinciaId') } value={watch('provinciaId') || ''}>
                        <option value="">Seleccionar provincia...</option>
                        {provinces.map(p => (
                          <option key={p.id} value={p.id}>{p.nombre || p.name}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="localidadId">Localidad</Label>
                      <Select id="localidadId" {...register('localidadId') } value={watch('localidadId') || ''}>
                        <option value="">Seleccionar localidad...</option>
                        {isLoadingLocalities ? (
                          <option value="">Cargando...</option>
                        ) : (
                          localities.map(l => (
                            <option key={l.id} value={l.id}>{l.nombre || l.name}</option>
                          ))
                        )}
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cp">Código Postal (CP)</Label>
                      <Input id="cp" placeholder="1043" {...register('cp')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="activo">Activo</Label>
                      <Select id="activo" {...register('activo')} value={watch('activo') || ''}>
                        <option value="true">Activo</option>
                        <option value="false">Inactivo</option>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <button
                        type="button"
                        onClick={() => setShowGeo(prev => !prev)}
                        className="text-sm font-medium text-[rgb(var(--brand))] hover:underline"
                      >
                        {showGeo ? 'Ocultar' : 'Mostrar'} geolocalización
                      </button>
                      {showGeo ? (
                        <div className="mt-2 grid gap-3 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="lat">Latitud</Label>
                            <Input id="lat" placeholder="-34.6037" {...register('lat')} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lng">Longitud</Label>
                            <Input id="lng" placeholder="-58.3816" {...register('lng')} />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {activeTab === 'contacto' && (
                  <ContactoTab data={contactosData} onChange={setContactosData} />
                )}

                {activeTab === 'domicilios' && (
                  <DomiciliosTab
                    data={domiciliosData}
                    onChange={setDomiciliosData}
                    provinces={provinces}
                  />
                )}

                {activeTab === 'comercial' && (
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Campos comerciales mínimos: expandir según necesidad */}
                    <div className="space-y-2">
                      <Label htmlFor="plazoPago">Plazo de pago (días)</Label>
                      <Input id="plazoPago" placeholder="30" {...register('plazoPago')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="limiteCredito">Límite de crédito</Label>
                      <Input id="limiteCredito" placeholder="0.00" {...register('limiteCredito')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="condicionPago">Condición de pago</Label>
                      <Select id="condicionPago" {...register('condicionPago')} value={watch('condicionPago') || 'CONTADO'}>
                        <option value="CONTADO">Contado</option>
                        <option value="CUENTA_CORRIENTE">Cuenta corriente</option>
                        <option value="MIXTO">Mixto</option>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="diasVencimiento">Días vencimiento</Label>
                      <Input id="diasVencimiento" placeholder="15" {...register('diasVencimiento')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="toleranciaVencidos">Tolerancia vencidos</Label>
                      <Input id="toleranciaVencidos" placeholder="3" {...register('toleranciaVencidos')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="prioridadOperativa">Prioridad operativa</Label>
                      <Select id="prioridadOperativa" {...register('prioridadOperativa')} value={watch('prioridadOperativa') || 'NORMAL'}>
                        <option value="NORMAL">Normal</option>
                        <option value="ALTA">Alta</option>
                        <option value="VIP">VIP</option>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bloqueoPorDeuda">Bloqueo por deuda</Label>
                      <Select
                        id="bloqueoPorDeuda"
                        {...register('bloqueoPorDeuda')}
                        value={watch('bloqueoPorDeuda') || 'false'}
                      >
                        <option value="false">No</option>
                        <option value="true">Sí</option>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="umbralDeuda">Umbral deuda</Label>
                      <Input id="umbralDeuda" placeholder="150000" {...register('umbralDeuda')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slaDespachoHs">SLA despacho (hs)</Label>
                      <Input id="slaDespachoHs" placeholder="12" {...register('slaDespachoHs')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slaEntregaHs">SLA entrega (hs)</Label>
                      <Input id="slaEntregaHs" placeholder="24" {...register('slaEntregaHs')} />
                    </div>
                    <div className="space-y-2" role="group" aria-label="Sucursal responsable">
                      <Label>Sucursal responsable</Label>
                      <input type="hidden" {...register('sucursal')} />
                      <Combobox
                        options={sucursalOptions}
                        value={watch('sucursal') || ''}
                        onChange={value => setValue('sucursal', value, { shouldDirty: true, shouldValidate: true })}
                        placeholder={loadingSucursales ? 'Cargando sucursales...' : 'Seleccionar sucursal...'}
                        searchPlaceholder="Buscar por código, nombre o localidad..."
                        disabled={loadingSucursales || Boolean(sucursalesError)}
                      />
                      {sucursalesError ? <p role="alert" className="text-sm text-red-600">{sucursalesError}</p> : null}
                    </div>
                  </div>
                )}

                {activeTab === 'subcuentas' && isEditing ? (
                  <div className="space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-[rgba(var(--muted),0.9)]">Las subcuentas heredan la configuración del cliente salvo los overrides explícitos. No se eliminan físicamente.</p>
                      <Button type="button" variant="ghost" onClick={() => setEditingSubcuenta({ codigo: '', nombre: '', descripcion: '', activo: true, sucursalResponsableId: '', condicionPago: '', observaciones: '' })}>Nueva subcuenta</Button>
                    </div>
                    <div className="overflow-x-auto rounded-xl border border-[rgba(var(--border),0.6)]">
                      <table className="w-full min-w-[720px] text-left text-sm">
                        <thead className="bg-[rgba(var(--subpanel),0.7)] text-xs uppercase text-[rgba(var(--muted),0.9)]"><tr><th className="px-3 py-2">Código</th><th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Sucursal responsable</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2 text-right">Acciones</th></tr></thead>
                        <tbody className="divide-y divide-[rgba(var(--border),0.5)]">
                          {subcuentasData.length === 0 ? <tr><td colSpan="5" className="px-3 py-6 text-center text-[rgba(var(--muted),0.9)]">Todavía no hay subcuentas para este cliente.</td></tr> : subcuentasData.map((subcuenta) => {
                            const sucursal = sucursales.find((item) => String(item.id) === String(subcuenta.sucursalResponsableId));
                            return <tr key={subcuenta.id}><td className="px-3 py-2 font-medium">{subcuenta.codigo}</td><td className="px-3 py-2">{subcuenta.nombre}</td><td className="px-3 py-2">{sucursal?.nombre || sucursal?.name || sucursal?.label || '—'}</td><td className="px-3 py-2"><span className="rounded-full border px-2 py-0.5 text-xs">{subcuenta.activo ? 'Activa' : 'Inactiva'}</span></td><td className="px-3 py-2 text-right"><Button type="button" variant="ghost" className="mr-1 min-h-0 px-2 py-1 text-xs" onClick={() => setEditingSubcuenta({ ...subcuenta })}>Editar</Button><Button type="button" variant="ghost" className="min-h-0 px-2 py-1 text-xs" onClick={() => toggleSubcuenta(subcuenta)}>{subcuenta.activo ? 'Desactivar' : 'Activar'}</Button></td></tr>;
                          })}
                        </tbody>
                      </table>
                    </div>
                    {editingSubcuenta ? <div className="space-y-3 rounded-xl border border-dashed border-[rgba(var(--border),0.7)] p-4"><div className="flex items-center justify-between"><h3 className="font-semibold">{editingSubcuenta.id ? 'Editar subcuenta' : 'Nueva subcuenta'}</h3><Button type="button" variant="ghost" className="min-h-0 px-2 py-1 text-xs" onClick={() => setEditingSubcuenta(null)}>Cancelar</Button></div><div className="grid gap-3 md:grid-cols-2"><div><Label htmlFor="subcuenta-codigo">Código *</Label><Input id="subcuenta-codigo" value={editingSubcuenta.codigo || ''} onChange={(event) => setEditingSubcuenta((current) => ({ ...current, codigo: event.target.value }))} /></div><div><Label htmlFor="subcuenta-nombre">Nombre *</Label><Input id="subcuenta-nombre" value={editingSubcuenta.nombre || ''} onChange={(event) => setEditingSubcuenta((current) => ({ ...current, nombre: event.target.value }))} /></div><div><Label htmlFor="subcuenta-sucursal">Sucursal responsable</Label><Select id="subcuenta-sucursal" value={editingSubcuenta.sucursalResponsableId || ''} onChange={(event) => setEditingSubcuenta((current) => ({ ...current, sucursalResponsableId: event.target.value }))}><option value="">Hereda del cliente</option>{sucursales.map((sucursal) => <option key={sucursal.id} value={sucursal.id}>{sucursal.nombre || sucursal.name || sucursal.label}</option>)}</Select></div><div><Label htmlFor="subcuenta-condicion-pago">Condición de pago</Label><Input id="subcuenta-condicion-pago" value={editingSubcuenta.condicionPago || ''} placeholder="Hereda del cliente" onChange={(event) => setEditingSubcuenta((current) => ({ ...current, condicionPago: event.target.value }))} /></div><div className="md:col-span-2"><Label htmlFor="subcuenta-descripcion">Descripción</Label><Input id="subcuenta-descripcion" value={editingSubcuenta.descripcion || ''} onChange={(event) => setEditingSubcuenta((current) => ({ ...current, descripcion: event.target.value }))} /></div><div className="md:col-span-2"><Label htmlFor="subcuenta-observaciones">Observaciones</Label><Input id="subcuenta-observaciones" value={editingSubcuenta.observaciones || ''} onChange={(event) => setEditingSubcuenta((current) => ({ ...current, observaciones: event.target.value }))} /></div></div><div className="flex justify-end"><Button type="button" variant="ghost" onClick={() => saveSubcuenta(editingSubcuenta)}>Guardar subcuenta</Button></div></div> : null}
                  </div>
                ) : null}

                {activeTab === 'notificaciones' && (
                  <NotificacionesTab data={notificacionesData} onChange={setNotificacionesData} />
                )}

                {activeTab === 'tarifas' && (
                  <TarifasTab data={tarifasData} onChange={setTarifasData} />
                )}

                {activeTab === 'retornables' && showReturnablesTab ? (
                  <RetornablesMonitor clienteId={id} clienteNombre={data?.cliente?.razonSocial} mode="embedded" />
                ) : null}

                {activeTab === 'cuenta-corriente' && isEditing ? (
                  <AccountStatement clienteId={id} mode="embedded" />
                ) : null}

                {activeTab !== 'cuenta-corriente' && activeTab !== 'retornables' ? <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    className="border border-[rgba(var(--border),0.7)] bg-transparent text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.7)]"
                    onClick={() => navigate('/admin/clientes')}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="ghost"
                    className="border border-[rgb(var(--brand))] bg-[rgb(var(--brand))] text-[rgb(var(--fg))] shadow-[0_10px_30px_rgba(var(--shadow),0.18)] hover:bg-[rgb(var(--brand-700))] hover:border-[rgb(var(--brand-700))]"
                    disabled={isSubmitting}
                  >
                    {isEditing ? 'Guardar cambios' : 'Crear Cliente'}
                  </Button>
                </div> : null}
              </form>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
